import api from '@/services/api'
import type {
  AtualizarRascunhosRequest,
  AtualizarRascunhosResponse,
  ChecklistProdutoResumo,
  ConteudoChecklist,
  DecisaoPublicacaoRequest,
  ErroValidacaoChecklist,
  PublicarChecklistRequest,
  VersaoChecklistProduto,
} from '@/types/checklist'

/**
 * Editor do checklist documental VERSIONADO do produto (AN-PP3 §6.1, `ChecklistProdutoController` de
 * oportunidades). Leitura: back-office; escrita: só ADMIN (o backend dá 403 aos demais).
 *
 * A tela não calcula nada: número da versão (o rascunho já vem como N+1), status, diferença rascunho × vigente
 * e contagens da revinculação vêm do backend. Os controllers devolvem o DTO direto (sem envelope) e o
 * interceptor de `api` já entrega o corpo.
 */

const base = (produtoId: string) => `/api/v1/oportunidades/checklists/produto/${produtoId}`

export const checklistProdutoService = {
  /** Vigente, rascunho, histórico (mais nova primeiro), diferença e `semChecklist`. */
  obter: async (produtoId: string): Promise<ChecklistProdutoResumo> => {
    const r = (await api.get(base(produtoId))) as unknown as ChecklistProdutoResumo | null
    return {
      produtoId: r?.produtoId ?? produtoId,
      produtoCodigo: r?.produtoCodigo ?? null,
      vigente: r?.vigente ?? null,
      rascunho: r?.rascunho ?? null,
      historico: r?.historico ?? [],
      diferencaRascunho: r?.diferencaRascunho ?? null,
      semChecklist: r?.semChecklist ?? null,
      pendente: r?.pendente ?? null,
      diferencaPendente: r?.diferencaPendente ?? null,
    }
  },

  /** Uma versão específica (inclusive SUBSTITUIDA); 404 `CHECKLIST_VERSAO_NAO_ENCONTRADA`. */
  versao: async (produtoId: string, versaoId: string): Promise<VersaoChecklistProduto> =>
    (await api.get(`${base(produtoId)}/versoes/${versaoId}`)) as unknown as VersaoChecklistProduto,

  /**
   * Cria ou substitui o rascunho (ADMIN). `versaoEsperada` = `revisao` do rascunho lido (0 = não havia) — se
   * outra pessoa salvou/publicou/descartou antes, 409 `CHECKLIST_CONFLITO_CONCORRENCIA`. 422 `CHECKLIST_INVALIDO`
   * traz a lista por campo.
   */
  salvarRascunho: async (
    produtoId: string,
    conteudo: ConteudoChecklist,
    versaoEsperada?: number,
  ): Promise<VersaoChecklistProduto> =>
    (await api.put(`${base(produtoId)}/rascunho`, conteudo, {
      params: versaoEsperada == null ? undefined : { versaoEsperada },
    })) as unknown as VersaoChecklistProduto,

  /** Descarta o rascunho (ADMIN) — 204. */
  descartarRascunho: async (produtoId: string): Promise<void> => {
    await api.delete(`${base(produtoId)}/rascunho`)
  },

  /**
   * Envia o rascunho para APROVAÇÃO (ADMIN; V64/AN-PP6 §3): 202 com `status: PENDENTE_APROVACAO`. A vigente só muda
   * quando outra pessoa aprovar. 409 `PROPOSTA_JA_PENDENTE` se já há uma proposta aberta.
   */
  publicar: async (produtoId: string, request: PublicarChecklistRequest): Promise<VersaoChecklistProduto> =>
    (await api.post(`${base(produtoId)}/publicar`, request)) as unknown as VersaoChecklistProduto,

  /**
   * Aprova a proposta INDICADA (revisão PP6-valr, A1: `…/versoes/{versaoId}/aprovar` + `conteudoHash` do que foi
   * revisado). GERENTE/ADMIN que NÃO seja o proponente (403 `APROVACAO_PELO_PROPONENTE`); 409 `PROPOSTA_DIVERGENTE`
   * se a proposta não é mais a pendente ou o conteúdo mudou.
   */
  aprovar: async (
    produtoId: string,
    versaoId: string,
    request: DecisaoPublicacaoRequest,
  ): Promise<VersaoChecklistProduto> =>
    (await api.post(`${base(produtoId)}/versoes/${versaoId}/aprovar`, request)) as unknown as VersaoChecklistProduto,

  /** Rejeita a proposta indicada com motivo (mesmas regras de quem aprova). */
  rejeitar: async (
    produtoId: string,
    versaoId: string,
    request: DecisaoPublicacaoRequest,
  ): Promise<VersaoChecklistProduto> =>
    (await api.post(`${base(produtoId)}/versoes/${versaoId}/rejeitar`, request)) as unknown as VersaoChecklistProduto,

  /** O PRÓPRIO proponente retira a proposta (revisão PP6-valr, 8b): REJEITADA "Retirada pelo proponente". */
  retirar: async (produtoId: string, versaoId: string): Promise<VersaoChecklistProduto> =>
    (await api.post(`${base(produtoId)}/versoes/${versaoId}/retirar`)) as unknown as VersaoChecklistProduto,

  /** Revincula à vigente as reservas RASCUNHO do produto (ADMIN). */
  atualizarRascunhos: async (
    produtoId: string,
    request: AtualizarRascunhosRequest,
  ): Promise<AtualizarRascunhosResponse> =>
    (await api.post(`${base(produtoId)}/atualizar-rascunhos`, request)) as unknown as AtualizarRascunhosResponse,
}

// ─── Erros (envelope `RequisicaoFalhou` de `api`: `data` é o ApiResponse do backend) ───

export const codigoErroChecklist = (e: unknown): string | undefined => {
  const data = (e as { data?: { errorCode?: unknown } } | null)?.data
  return typeof data?.errorCode === 'string' ? data.errorCode : undefined
}

/** Lista `[{campo, codigo, mensagem}]` do 422 `CHECKLIST_INVALIDO`; vazia nos demais erros. */
export const errosValidacaoChecklist = (e: unknown): ErroValidacaoChecklist[] => {
  if (codigoErroChecklist(e) !== 'CHECKLIST_INVALIDO') return []
  const lista = (e as { data?: { data?: unknown } } | null)?.data?.data
  if (!Array.isArray(lista)) return []
  return lista.filter(
    (x): x is ErroValidacaoChecklist =>
      !!x && typeof x === 'object' && typeof (x as ErroValidacaoChecklist).campo === 'string',
  )
}

const TEXTO_ERRO: Record<string, string> = {
  CHECKLIST_INVALIDO: 'O checklist tem campos a corrigir.',
  PRODUTO_SEM_CHECKLIST: 'Este produto não tem checklist documental.',
  PRODUTO_RETIRADO: 'Produto retirado do catálogo: o checklist não pode mais ser alterado.',
  PRODUTO_DESCONHECIDO: 'Produto ainda não conhecido pelo módulo de oportunidades. Tente de novo em instantes.',
  CHECKLIST_CONFLITO_CONCORRENCIA:
    'Outra pessoa alterou o checklist deste produto ao mesmo tempo. Recarregue e confira antes de salvar de novo.',
  MOTIVO_OBRIGATORIO: 'Informe o motivo.',
  CHECKLIST_SEM_RASCUNHO: 'Não há rascunho para publicar. Recarregue a página.',
  CHECKLIST_RASCUNHO_ABERTO: 'Há um rascunho aberto no editor: publique ou descarte antes.',
  CHECKLIST_SEM_ALTERACAO: 'O rascunho é igual à versão vigente — não há o que publicar.',
  CHECKLIST_VERSAO_NAO_ENCONTRADA: 'Versão não encontrada para este produto.',
  PROPOSTA_JA_PENDENTE: 'Já há uma versão aguardando aprovação: aprove ou rejeite antes de enviar outra.',
  PROPOSTA_PENDENTE_NAO_ENCONTRADA: 'A proposta já foi decidida por outra pessoa. Recarregue a página.',
  APROVACAO_PELO_PROPONENTE: 'Quem enviou a versão não pode aprová-la nem rejeitá-la: outra pessoa decide.',
  APROVADOR_SEM_PAPEL: 'Só gerente ou administrador aprova o checklist.',
  CONFLITO_VERSAO: 'A proposta mudou desde que você abriu a página. Recarregue e confira.',
  MOTIVO_REJEICAO_OBRIGATORIO: 'Informe o motivo da rejeição.',
}

/** Texto legível do erro; a mensagem do backend vai junto quando acrescenta algo. */
export const mensagemErroChecklist = (e: unknown, padrao: string): string => {
  const codigo = codigoErroChecklist(e)
  const status = (e as { status?: number } | null)?.status
  const backend = (e as { message?: unknown } | null)?.message
  const doBackend = typeof backend === 'string' && backend.trim() ? backend : undefined
  if (codigo && codigo in TEXTO_ERRO) {
    const texto = TEXTO_ERRO[codigo]
    return doBackend && doBackend !== texto && codigo !== 'CHECKLIST_INVALIDO' ? `${texto} (${doBackend})` : texto
  }
  if (status === 403) return 'Só o administrador altera o checklist do produto.'
  return doBackend ?? padrao
}
